import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { CSS_CLASS_PREFIX } from "../../constants/css";
import { arrayIsSame } from "../../lib/utils";
import { cssMainSelector } from "../../lib/cssSelectors";
import { withTranslation } from "react-i18next";
import MaterialIcon from "../MaterialIcon";

const Container = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    padding-bottom: ${p => p.theme.spacing.md};
  }
`;

const Legenda = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-bottom: ${p => p.theme.spacing.md};
    width: 100%;
  }
`;

const Title = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    font-weight: ${p => p.theme.font.weightBold};
    order: 0;
  }
`;

const SelectAll = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    color: ${p => p.theme.filter.selectAllLinkColor};
    cursor: pointer;
    font-size: ${p => p.theme.font.sizeSm};
    order: 1;
  }
`;

const checkboxContainerLabel1 = `label.${CSS_CLASS_PREFIX}-show:hover input ~ .${CSS_CLASS_PREFIX}-checkmark-border .${CSS_CLASS_PREFIX}-checkmark`;
const checkboxContainerLabel2 = `label.${CSS_CLASS_PREFIX}-show input:checked ~ .${CSS_CLASS_PREFIX}-checkmark-border .${CSS_CLASS_PREFIX}-checkmark`;
const checkboxContainerLabel3 = `label.${CSS_CLASS_PREFIX}-show input:checked ~ .${CSS_CLASS_PREFIX}-checkmark-border .${CSS_CLASS_PREFIX}-checkmark:after`;
const CheckboxContainer = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
  
    * {
      box-sizing: content-box;
    }
    
    label.${CSS_CLASS_PREFIX}-show {
      box-sizing: content-box;
      cursor: pointer;
      display: block;
      font-size: ${p => p.theme.font.size};
      margin-bottom: ${p => p.theme.spacing.sm};
      padding-left: 22px;
      position: relative;
      user-select: none;

      input {
        cursor: pointer;
        opacity: 0;
        position: absolute;
      }
    }

    ${checkboxContainerLabel1} {
      background-color: ${p =>
        p.theme.filter.checkboxList.hoverBackgroundColor};
      box-sizing: content-box;
    }

    ${checkboxContainerLabel2} {
      background-color: ${p =>
        p.theme.filter.checkboxList.checkedBackgroundColor};
      box-sizing: content-box;
    }
    
    ${checkboxContainerLabel3} {
      box-sizing: content-box;
      display: block;
    }

    label.${CSS_CLASS_PREFIX}-hidden {
      display: none;
    }
  }
`;

const CheckmarkBorder = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    border: solid;
    border-color: ${p => p.theme.filter.checkboxList.borderColor};
    border-width: 1px;
    height: 14px;
    left: 0;
    position: absolute;
    top: 0;
    width: 14px;
  }
`;

const Checkmark = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.color.white};
    height: 14px;
    left: 0;
    position: absolute;
    top: 0;
    width: 14px;

    :after {
      border: solid;
      border-color: ${p => p.theme.filter.checkboxList.checkColor};
      border-width: 0 2px 2px 0;
      content: "";
      display: none;
      height: 8px;
      left: 4px;
      position: absolute;
      top: 1px;
      transform: rotate(45deg);
      width: 4px;
    }
  }
`;

const CollapseLink = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    * {
      color: ${p => p.theme.filter.collapseLinkColor};
      cursor: pointer;
      padding-top: ${p => p.theme.spacing.sm};
      user-select: none;
    }
  }
`;

class CheckboxListFilter extends PureComponent {
  render() {
    const {
      onSelectAllClick,
      onCollapseClick,
      showSelectAll,
      title
    } = this.props;
    let checkboxes = this.checkboxes();

    return (
      <Container>
        <Legenda>
          <Title>{title}</Title>
          {showSelectAll && (
            <SelectAll onClick={onSelectAllClick}>
              {this.selectAllText()}
            </SelectAll>
          )}
        </Legenda>
        <CheckboxContainer>{checkboxes}</CheckboxContainer>
        <CollapseLink onClick={onCollapseClick}>
          {this.collapseText()}
        </CollapseLink>
      </Container>
    );
  }

  checkboxes() {
    const { collapsed, maxShow, onChange, value, values } = this.props;
    const checkboxes = [];
    let i = 0;

    for (const [checkboxValue, checkboxLabel] of values) {
      let labelClass = `${CSS_CLASS_PREFIX}-show`;

      i += 1;
      if (collapsed && maxShow && i > maxShow) {
        labelClass = `${CSS_CLASS_PREFIX}-hidden`;
      }

      checkboxes.push(
        <label className={labelClass} key={checkboxValue}>
          {checkboxLabel}
          <input
            name={checkboxValue}
            type="checkbox"
            checked={value.indexOf(checkboxValue.toString()) >= 0}
            onChange={onChange}
          />
          <CheckmarkBorder className={`${CSS_CLASS_PREFIX}-checkmark-border`}>
            <Checkmark className={`${CSS_CLASS_PREFIX}-checkmark`} />
          </CheckmarkBorder>
        </label>
      );
    }

    return checkboxes;
  }

  collapseText() {
    const { collapsed, maxShow, t, values } = this.props;

    if (maxShow && values.length > maxShow) {
      if (collapsed) {
        const count = values.length - maxShow;

        return (
          <span>
            <span>{t("unCollapseText", { count: count })}</span>{" "}
            <MaterialIcon>arrow_drop_down</MaterialIcon>
          </span>
        );
      } else {
        return (
          <span>
            {t("collapseText")} <MaterialIcon>arrow_drop_up</MaterialIcon>
          </span>
        );
      }
    } else {
      return "";
    }
  }

  selectAllText() {
    const { t, values, value } = this.props;
    const allIds = values.map(x => x[0]);

    if (arrayIsSame(value, allIds)) {
      return "";
    } else {
      return t("selectAll");
    }
  }
}

CheckboxListFilter.propTypes = {
  collapsed: PropTypes.bool,
  maxShow: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onCollapseClick: PropTypes.func,
  onSelectAllClick: PropTypes.func,
  showSelectAll: PropTypes.bool,
  title: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  values: PropTypes.array.isRequired
};

CheckboxListFilter.defaultProps = {
  showSelectAll: true
};

export default withTranslation(["filters"])(CheckboxListFilter);
